
import React from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Search, Filter, Grid, List } from 'lucide-react';

export type CountryFilterType = 'active' | 'coming-soon' | 'all';
export type ViewType = 'list' | 'region';

interface Region {
  id: string;
  name: string;
  code: string;
}

interface CountryFiltersProps {
  searchQuery: string;
  onSearchChange: (search: string) => void;
  filter: CountryFilterType;
  onFilterChange: (filter: CountryFilterType) => void;
  selectedRegion: string | null;
  onRegionChange: (region: string | null) => void;
  viewType: ViewType;
  onViewTypeChange: (view: ViewType) => void;
  availableRegions: Region[];
}

const CountryFilters: React.FC<CountryFiltersProps> = ({
  searchQuery,
  onSearchChange,
  filter,
  onFilterChange,
  selectedRegion,
  onRegionChange,
  viewType,
  onViewTypeChange,
  availableRegions,
}) => {
  
  // Debug logging
  console.log('🔍 CountryFilters - availableRegions:', availableRegions);

  const handleRegionChange = (value: string) => {
    onRegionChange(value === 'all' ? null : value);
  };

  return (
    <div className="flex flex-col items-center space-y-4 mb-8">
      {/* Search Bar */}
      <div className="relative w-full max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search countries..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Filter Controls */}
      <div className="flex flex-wrap justify-center gap-4">
        {/* Status Filter */}
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-muted-foreground" />
          <Select value={filter} onValueChange={onFilterChange}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Countries</SelectItem>
              <SelectItem value="active">Active Countries</SelectItem>
              <SelectItem value="coming-soon">Coming Soon</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Region Filter */}
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-muted-foreground" />
          <Select value={selectedRegion || 'all'} onValueChange={handleRegionChange}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Regions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Regions</SelectItem>
              <SelectItem value="no-region">No Region</SelectItem>
              {(availableRegions || []).map((region) => (
                <SelectItem key={region.id} value={region.id}>
                  {region.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* View Toggle */}
        <div className="flex items-center gap-1 border rounded-md p-1">
          <Button
            variant={viewType === 'list' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onViewTypeChange('list')}
            className="h-8 px-3"
          >
            <List size={16} className="mr-1" />
            List
          </Button>
          <Button
            variant={viewType === 'region' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onViewTypeChange('region')}
            className="h-8 px-3"
          >
            <Grid size={16} className="mr-1" />
            Region
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CountryFilters;
